"use client"

import { useMemo, useState } from "react"
import Link from "next/link"
import { ArrowUpRight, Info, RefreshCw } from "lucide-react"
import {
  StatBorderGrid,
  ToolPageShell,
  ToolProductCarousel,
  ToolShareBar,
} from "@/components/brand-kit"
import { TOOL_METADATA } from "@/lib/tool-metadata"
import { calculateRefrigeratorSize, REFRIGERATOR_SIZE_DEFAULTS } from "@/lib/tools/refrigerator-size"
import { FAMILY_OF_FOUR_SIZE } from "@/lib/refrigerator-answer-facts"
import { EmbedThisCard } from "@/components/data/embed-this-card"

const FALLBACK_RELATED = [
  "energy-cost-calculator",
  "refrigerator-lifespan-calculator",
  "kitchen-space-planner",
  "cooking-temperature-converter",
]

function buildRelated(slugs: string[], currentSlug: string) {
  const seen = new Set<string>([currentSlug])
  const out: { title: string; category: string; description: string; href: string; time: string }[] = []
  const candidates = [...slugs, ...FALLBACK_RELATED]
  for (const s of candidates) {
    if (out.length === 4) break
    if (seen.has(s)) continue
    const m = TOOL_METADATA[s]
    if (!m) continue
    seen.add(s)
    out.push({
      title: m.name,
      category: m.category,
      description: m.shortDescription,
      href: `/tools/${s}`,
      time: "~45 sec",
    })
  }
  return out
}

export function RefrigeratorSizeCalculatorPageClient() {
  const meta = TOOL_METADATA["refrigerator-size-calculator"]

  const [householdSize, setHouseholdSize] = useState<string>(REFRIGERATOR_SIZE_DEFAULTS.householdSize)
  const [cookingFrequency, setCookingFrequency] = useState<string>(REFRIGERATOR_SIZE_DEFAULTS.cookingFrequency)
  const [shoppingFrequency, setShoppingFrequency] = useState<string>(REFRIGERATOR_SIZE_DEFAULTS.shoppingFrequency)

  // The calculation lives in lib/tools/refrigerator-size.ts (FRIDGE-356) so the
  // /embed/size-calculator widget and this page can never disagree; the logic
  // there is the legacy page-client's, moved verbatim.
  const result = useMemo(
    () => calculateRefrigeratorSize({ householdSize, cookingFrequency, shoppingFrequency }),
    [householdSize, cookingFrequency, shoppingFrequency]
  )

  return (
    <ToolPageShell
      toolName={meta.name}
      heroEyebrow={meta.category}
      heroTitlePre="What size fridge does"
      heroTitleItalic="your"
      heroTitlePost="household actually need?"
      // FRIDGE-1463: the first sentence answers the question in the visitor's
      // words, with the one family-of-four figure the site prints everywhere
      // (FRIDGE-1435); the tool description follows it.
      heroSubtitle={`${FAMILY_OF_FOUR_SIZE.answer} ${meta.mediumDescription}`}
      heroMeta={["⏱ ~30 sec", "Household planning estimate"]}
      heroShare={<ToolShareBar title={meta.name} />}
      heroRight={
        <StatBorderGrid
          stats={[
            { value: "4", label: "Cu ft per person", note: "Starting estimate" },
            { value: "+4", label: "Daily cooking", note: "Household allowance" },
            { value: "+10", label: "Monthly bulk shop", note: "Adjustment" },
            { value: "+5", label: "Upper range, cu ft", note: "Above the estimate" },
          ]}
          className="w-full max-w-md md:max-w-none md:w-auto mx-auto md:mx-0"
        />
      }
      // FIX-10 parity: render the FULL metadata FAQ set — the deleted sr-only
      // zone used to carry the items beyond the first six.
      faqs={meta.faqs.map((f) => ({ question: f.question, answer: f.answer }))}
      relatedTools={buildRelated(meta.relatedTools, "refrigerator-size-calculator")}
      cta={{
        title: "Ready to shop the size that fits your life?",
        body: "Browse French door, top-freezer, side-by-side, and counter-depth fridges — every type, sortable by cubic-foot capacity.",
        buttonLabel: "Browse all refrigerators",
        buttonHref: "/best-refrigerators",
      }}
    >
      {/* ============ CALCULATOR ============ */}
      <section className="bg-gray-50 py-8 md:py-12">
        <div className="max-w-[1120px] mx-auto px-6 md:px-8 space-y-6">
          <div className="grid lg:grid-cols-2 gap-6 lg:items-stretch">
            {/* Input card */}
            <div className="bg-white rounded-xl p-6 md:p-7 border border-gray-900 flex flex-col">
              <div className="flex items-center gap-3 mb-2">
                <span className="font-mono text-[12px] font-bold tracking-[0.15em] text-[#ff9951]">
                  STEP 01
                </span>
                <div className="flex-1 h-[1px] bg-gray-300" />
              </div>
              <h2
                className="text-gray-900 font-black"
                style={{ fontSize: 28, letterSpacing: "-0.025em", lineHeight: 1.05 }}
              >
                Three inputs.
                <br />
                One real answer.
              </h2>
              <p className="text-[14px] text-gray-600 mt-2 leading-[1.5]">
                Start with 4 cu ft per person, then add the cooking and shopping allowances below.
              </p>

              <div className="mt-7 space-y-5">
                <div>
                  <label htmlFor="household-size" className="block text-[11px] font-bold uppercase tracking-[0.12em] text-gray-900 mb-2">
                    Household size
                  </label>
                  <select
                    id="household-size"
                    value={householdSize}
                    onChange={(e) => setHouseholdSize(e.target.value)}
                    className="tool-input-clean w-full px-4 py-4 text-[18px] font-bold rounded-lg outline-none border border-gray-300 focus:border-[#ff9951] bg-white"
                  >
                    <option value="1">1 person</option>
                    <option value="2">2 people</option>
                    <option value="3">3 people</option>
                    <option value="4">4 people</option>
                    <option value="5">5 people</option>
                    <option value="6">6 people</option>
                  </select>
                  <div className="text-[11px] text-gray-500 mt-1.5 flex items-center gap-1.5">
                    <Info className="w-3.5 h-3.5" /> Counts everyone who eats from this fridge daily.
                  </div>
                </div>

                <div>
                  <label htmlFor="cooking-frequency" className="block text-[11px] font-bold uppercase tracking-[0.12em] text-gray-900 mb-2">
                    Cooking frequency
                  </label>
                  <select
                    id="cooking-frequency"
                    value={cookingFrequency}
                    onChange={(e) => setCookingFrequency(e.target.value)}
                    className="tool-input-clean w-full px-4 py-4 text-[18px] font-bold rounded-lg outline-none border border-gray-300 focus:border-[#ff9951] bg-white"
                  >
                    <option value="rarely">Rarely (eat out mostly)</option>
                    <option value="weekly">Few times a week</option>
                    <option value="daily">Daily cooking</option>
                  </select>
                </div>

                <div>
                  <label htmlFor="shopping-frequency" className="block text-[11px] font-bold uppercase tracking-[0.12em] text-gray-900 mb-2">
                    Grocery shopping frequency
                  </label>
                  <select
                    id="shopping-frequency"
                    value={shoppingFrequency}
                    onChange={(e) => setShoppingFrequency(e.target.value)}
                    className="tool-input-clean w-full px-4 py-4 text-[18px] font-bold rounded-lg outline-none border border-gray-300 focus:border-[#ff9951] bg-white"
                  >
                    <option value="daily">Daily / as needed</option>
                    <option value="weekly">Weekly</option>
                    <option value="biweekly">Every 2 weeks</option>
                    <option value="monthly">Monthly bulk shopping</option>
                  </select>
                </div>

                <div className="pt-2 text-[11px] uppercase tracking-[0.12em] text-gray-500 font-bold flex items-center gap-1.5">
                  <RefreshCw className="w-3 h-3" /> Updates live as you type
                </div>
              </div>
            </div>

            {/* Result panel */}
            <div className="flex flex-col">
              <div
                className="rounded-2xl p-6 md:p-8 relative overflow-hidden text-white flex-1"
                style={{ background: "linear-gradient(135deg, #0469f0 0%, #0053c4 100%)" }}
              >
                <div
                  aria-hidden
                  className="absolute top-0 right-0 w-[340px] h-[340px] rounded-full pointer-events-none"
                  style={{
                    background: "rgba(255,153,81,0.35)",
                    filter: "blur(60px)",
                    transform: "translate(25%, -35%)",
                  }}
                />
                <div className="relative">
                  <div className="flex items-center justify-between mb-8 flex-wrap gap-2">
                    <div className="text-[11px] font-bold uppercase tracking-[0.15em] text-white/70">
                      Recommended capacity
                    </div>
                    <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-[0.15em] text-white/60">
                      <RefreshCw className="w-3 h-3" /> Updated live
                    </div>
                  </div>
                  <div
                    className="font-black"
                    style={{
                      fontSize: "clamp(36px, 8vw, 88px)",
                      letterSpacing: "-0.045em",
                      lineHeight: 0.9,
                    }}
                  >
                    {result?.recommendedSize ?? 0}
                    <span className="text-[20px] md:text-[24px] font-black tracking-tighter text-white/60 ml-2">
                      cu ft
                    </span>
                  </div>
                  <div className="mt-4 text-[14px] text-white/80 leading-[1.5]">
                    {result?.recommendation ?? "Enter your household details."}
                  </div>
                  <div className="grid grid-cols-2 gap-2 sm:gap-3 mt-6 md:mt-8">
                    <div className="rounded-lg p-3 sm:p-4 bg-white/10 border border-white/15">
                      <div className="text-[10px] uppercase tracking-[0.15em] text-white/60 font-bold mb-1">
                        Min size
                      </div>
                      <div className="text-[18px] sm:text-[22px] font-black tracking-tight">
                        {result?.minSize ?? "—"} cu ft
                      </div>
                    </div>
                    <div className="rounded-lg p-3 sm:p-4 bg-white/10 border border-white/15">
                      <div className="text-[10px] uppercase tracking-[0.15em] text-white/60 font-bold mb-1">
                        Max size
                      </div>
                      <div className="text-[18px] sm:text-[22px] font-black tracking-tight">
                        {result?.maxSize ?? "—"} cu ft
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============ FIRST CAROUSEL ============ */}
      <section className="bg-white py-4 md:py-6">
        <ToolProductCarousel
          eyebrow="Step 02 · Shop the size"
          title="Explore French-door refrigerators"
          description="Compare each model’s capacity and dimensions with your estimate. This selection is not filtered to your calculated range."
          collectionType="french-door"
          fallbackCollectionType="bottom-freezer"
        />
      </section>

      {/* ============ METHODOLOGY ============ */}
      <section className="bg-gray-50 py-8 md:py-12 border-y border-gray-200">
        <div className="max-w-[1120px] mx-auto px-6 md:px-8">
          <div className="grid md:grid-cols-[1fr_1.3fr] gap-8 md:gap-12 items-start">
            <div>
              <div className="text-[11px] font-bold uppercase tracking-[0.15em] mb-3 text-[#ff9951]">
                Under the hood
              </div>
              <h2
                className="text-gray-900 font-black"
                style={{ fontSize: "clamp(24px, 3vw, 36px)", letterSpacing: "-0.03em", lineHeight: 1.05 }}
              >
                How the <em className="italic text-[#ff9951]">estimate</em> is calculated.
              </h2>
              <p className="text-[14px] text-gray-600 mt-4 max-w-[400px] leading-[1.55]">
                This Fridge.com planning formula uses the three inputs above. It estimates capacity;
                it does not measure your food storage or verify that a refrigerator fits your kitchen.
              </p>
            </div>
            <ol className="list-none p-0 m-0 border-t border-gray-900">
              {[
                {
                  n: "01",
                  t: "Base capacity per person",
                  d: "Start with 4 cu ft for each person in the household.",
                },
                {
                  n: "02",
                  t: "Cooking & shopping adjustment",
                  d: "+2 cu ft for weekly cooking, +4 for daily cooking. +3/+6/+10 for weekly, biweekly, and monthly shopping.",
                },
                {
                  n: "03",
                  t: "Style recommendation",
                  d: "Below 20 cu ft: compact or top-freezer. 20 to under 25: French door or side-by-side. 25 to under 30: large French door. 30 or more: extra-large styles.",
                },
              ].map((s) => (
                <li
                  key={s.n}
                  className="grid grid-cols-[auto_1fr_auto] gap-4 sm:gap-7 items-center py-5 sm:py-6 border-b border-gray-900"
                >
                  <div className="font-mono text-sm font-bold tracking-[0.05em] min-w-[28px] sm:min-w-[34px] text-[#ff9951]">
                    {s.n}
                  </div>
                  <div>
                    <h3
                      className="text-gray-900"
                      style={{ fontSize: 22, fontWeight: 800, letterSpacing: "-0.02em" }}
                    >
                      {s.t}
                    </h3>
                    <p className="mt-1.5 text-[14px] text-gray-600 leading-[1.55]">{s.d}</p>
                  </div>
                  <ArrowUpRight className="w-4 h-4 sm:w-5 sm:h-5 text-gray-500 flex-shrink-0" />
                </li>
              ))}
            </ol>
          </div>
        </div>
      </section>

      {/* ============ SHOP BY WIDTH (FRIDGE-1484) ============ */}
      <section className="bg-white py-8 md:py-10">
        <div className="max-w-[1120px] mx-auto px-6 md:px-8">
          <div className="flex items-center gap-3 mb-3">
            <span className="font-mono text-[12px] font-bold tracking-[0.15em] text-[#ff9951]">BY WIDTH</span>
            <div className="flex-1 h-[1px] bg-gray-300" />
          </div>
          <h2 className="text-gray-900 font-black" style={{ fontSize: 24, letterSpacing: "-0.02em", lineHeight: 1.1 }}>
            Know your cutout? Shop refrigerators by recorded width.
          </h2>
          <p className="text-[14px] text-gray-600 mt-2 leading-[1.5] max-w-2xl">
            Every collection lists the models we track at that recorded width with the lowest tracked price on each. The median full-size refrigerator records 33.13 inches wide, and one in three will not clear a 30-inch doorway on either side.
          </p>
          <ul className="mt-4 flex flex-wrap gap-2">
            {[24, 28, 30, 32, 33, 34, 35, 36].map((w) => (
              <li key={w}>
                <Link
                  href={`/collections/${w}-inch-wide-refrigerators`}
                  className="inline-block rounded-full border border-gray-900 px-4 py-2 text-[14px] font-semibold text-gray-900 hover:bg-[#ff9951] hover:border-[#ff9951] hover:text-white"
                >
                  {w} inch wide refrigerators
                </Link>
              </li>
            ))}
            <li>
              <Link href="/refrigerator-sizing-guide" className="inline-block rounded-full border border-gray-300 px-4 py-2 text-[14px] font-semibold text-gray-700 hover:border-gray-900">
                Refrigerator sizes chart
              </Link>
            </li>
          </ul>
        </div>
      </section>

      {/* ============ EMBED THIS CALCULATOR (FRIDGE-356) ============ */}
      <section className="bg-white py-8 md:py-10">
        <div className="max-w-[1120px] mx-auto px-6 md:px-8">
          <EmbedThisCard asset="size-calculator" />
        </div>
      </section>

      {/* ============ SECOND CAROUSEL ============ */}
      <section className="bg-white py-4 md:py-6">
        <ToolProductCarousel
          eyebrow="You might also like"
          title="Side-by-side fridges for tight kitchens"
          description="Slimmer footprint, eye-level freezer access, capacities up to 28 cu ft."
          collectionType="side-by-side"
          fallbackCollectionType="top-freezer"
        />
      </section>
    </ToolPageShell>
  )
}
